import type { AxiosError } from "axios";
import { TEAMS_DIGEST_REFUSAL_CODES } from "@/types/msteams-link";
import type {
  TTeamsDigestRefusalCode,
  TTeamsDigestRefused,
  TTeamsDigestSendResult,
  TTeamsDigestSent,
  TTeamsWorkspaceLink,
} from "@/types/msteams-link";

/** **The one definition of "connected"** for a person's Teams link, so that nothing
 *  else has to spell it out.
 *
 *  `matched` and nothing more: a matched link is what a Microsoft sign-in produces, and
 *  it stays connected whether or not the Teams app is installed and whether or not the
 *  admin has paused the channel — in both of those the person has done everything they
 *  can, which is why `receiving` is the wrong test and always was. The other four
 *  states (`unlinked`, `uninstalled`, `blocked`, `unmatched`) are not.
 *
 *  Exported, and a function rather than a comparison spelled out per site, because the
 *  word is spent in five places that must agree: the row's *Connected* badge, the
 *  caption counting the workspaces they are also connected in, the Connect button's
 *  offer (negated), the install line (connected, but nothing to deliver to) and the
 *  nudge's list of workspaces messaging their colleagues and not them. Every one of
 *  them said it in its own words before NG-1622, which is five chances for the UI to
 *  use one word for two different facts.
 */
export function isConnected(row: TTeamsWorkspaceLink): boolean {
  return row.link?.state === "matched";
}

/** Whether a resolved send actually sent a card, narrowing the union as it goes.
 *
 *  `sent` is the wire's own discriminator — always `true` on the 202 and always
 *  `false` on the 200 — but the generated type widens both to `boolean`, so the
 *  narrowing has to be stated once here rather than being a property access the
 *  compiler understands. One definition, because the alternative is each call site
 *  reaching for `"delivery_id" in result` and two of them disagreeing about what
 *  "sent" means.
 */
export function isDigestSent(result: TTeamsDigestSendResult): result is TTeamsDigestSent {
  return result.sent;
}

/** The refusal code behind a failed send, or `null` for anything we have no sentence
 *  for — a network failure, a 500, or a code this build has never heard of.
 *
 *  The same shape as `teamsErrorCode` beside it and for the same reason: the value
 *  comes off the wire and becomes half of an i18n key, so it is matched against a
 *  closed set rather than trusted. A `null` is not a missing answer, it is the
 *  answer — it means the generic failure copy, which is the honest thing to say
 *  about a reason we cannot name.
 */
export function digestRefusalCode(error: unknown): TTeamsDigestRefusalCode | null {
  const data = (error as AxiosError<TTeamsDigestRefused>)?.response?.data;
  return TEAMS_DIGEST_REFUSAL_CODES.find((known) => known === data?.code) ?? null;
}
